import type { CSSProperties } from 'react';
import { memo, useEffect, useMemo } from 'react';
import type {
  AncestorFolder,
  NameMatch,
  VisualMatch,
} from '@frameio/graphql-client/generated';
import { date, plural } from '@frameio/i18n';
import { getThumbMedia } from '@frameio/shared/finder/utils';
import { getUploadDisplayDate } from '@frameio/shared/utils';
import { EMPTY_OBJECT } from '@frameio/utils';
import { convertMillisecondsToSeconds } from '@frameio/utils/formatters';
import type { ThemeSpaces } from '@frameio/vapor';
import {
  CommentCountLabel,
  MediaThumbnail,
  MediaThumbnailLabel,
  Text,
  theme,
  useActiveWindowBreakpoint,
  useIsListBoxItemSelected,
  VersionNumberLabel,
} from '@frameio/vapor';
import { IconLock } from '@frameio/vapor-icons';
import {
  ClipMatchTimeline,
  FolderThumb,
  HighlightText,
  ImageWrapper,
  MediaThumbnailContentBottom,
} from '../../../../../shared/components';
import { useTrackSearchResultsInspected } from '../../../../../shared/contexts/SearchAnalyticsContext';
import type { TrackTabSelected } from '../../../../../shared/contexts/SearchAnalyticsContext/types';
import type { QuickSearchAssetV2Fragment as QuickSearchAssetFragment } from '../../../../../shared/gql/generated/QuickSearchAssetV2';
import { useQuickSearchAssetData } from '../../../../../shared/hooks/useQuickSearchAssetData';
import type { SectionId } from '../../../../../shared/types';
import {
  getAssetDisplayCategory,
  getAssetDurationInfo,
  getFileType,
  getMatchTypeTrackingValues,
  getResultSourceFromSectionId,
  getValidAssetResultMatches,
} from '../../../../../shared/utils';
import { AssetContextMenuContent } from '../../../QuickSearchContextMenu';
import {
  LIST_ITEM_MEDIA_HEIGHT,
  LIST_ITEM_MEDIA_HEIGHT_MOBILE,
  LIST_ITEM_MEDIA_WIDTH,
  LIST_ITEM_MEDIA_WIDTH_MOBILE,
} from '../../constants';
import { getFieldMatch } from '../../utils';
import { FolderMediaContainer } from '../FolderMediaContainer';
import { ListItemCard } from '../ListItemCard';
import { ListItemDetailRow } from '../ListItemDetailRow';
import { useListItemContextMenu } from '../useListItemContextMenu';
import styles from './AssetListItem.module.css';
import { AssetPathname } from './AssetPathname';
import { FeaturedFieldMatches } from './FeaturedFieldMatches';
import TEXT from './generated/TEXT/AssetListItem';

type AssetListItemProps = {
  assetId: string;
  disableHoverStyles?: boolean;
  featuredMatchCount?: number;
  isActive?: boolean;
  isAdvancedSearch?: boolean;
  isCompact?: boolean;
  itemPosition: number;
  paddingInlineEnd?: ThemeSpaces;
  sectionId?: SectionId;
  shouldUseBuiltInContextMenu?: boolean;
  tabSelected?: TrackTabSelected;
};

TEXT.DEFINE({
  /** Label for restricted/private folder */
  restrictedFolder: 'Restricted Folder',
  /** Accessibility label for asset context menu button */
  contextMenuLabel: 'Asset context menu',
  /** Prefix label for version number (e.g., "V1") */
  versionPrefixLabel: 'V',
  /** Formatted display date for asset (e.g., "Jan 26, 2026") */
  formattedDate: (displayDate: Date): string => date(displayDate, 'medium'),
  /** Folder item count display with pluralization */
  itemCountLabel: ({ count }: { count: number }): string =>
    plural(count, `${count} items`, '1 item'),
  /** Accessible label for the comment count indicator (e.g. "5 comments"). */
  commentCountLabel: ({ count }: { count: number }): string =>
    plural(count, `${count} comments`, '1 comment'),
});

const FOLDER_HEIGHT = 65;
const FOLDER_HEIGHT_MOBILE = 43.3;
const FOLDER_WIDTH = 96;
const FOLDER_WIDTH_MOBILE = 64;

export const AssetListItem = memo(
  ({
    assetId,
    disableHoverStyles = false,
    featuredMatchCount,
    isActive: isActiveProp,
    isAdvancedSearch = false,
    isCompact = false,
    itemPosition,
    paddingInlineEnd,
    sectionId,
    shouldUseBuiltInContextMenu = true,
    tabSelected,
  }: AssetListItemProps) => {
    // Fetch asset data and appropriate matches based on section
    const { assetData, matches } = useQuickSearchAssetData({
      assetId,
      sectionId,
    });
    const trackSearchResultsInspected = useTrackSearchResultsInspected();
    const isActiveFromListBox = useIsListBoxItemSelected();
    const isActive = isActiveProp ?? isActiveFromListBox;

    const webSource = isAdvancedSearch ? 'FullScreen' : 'Modal';
    const isFolder = assetData?.__typename === 'FolderAsset';
    const isVersionStack = Number.isInteger(assetData?.versionNumber);

    const name = assetData?.name ?? '';
    const ancestorFolders = assetData?.ancestorFolders;
    const isFolderAsset = assetData?.__typename === 'FolderAsset';
    const isRestrictedFolder = isFolderAsset && assetData?.restricted;
    const commentCount = assetData?.commentCount ?? 0;

    const project = assetData?.project;

    const media = useMemo(
      () =>
        getThumbMedia(assetData as QuickSearchAssetFragment, {
          width: LIST_ITEM_MEDIA_WIDTH * 2,
          height: LIST_ITEM_MEDIA_HEIGHT * 2,
        }) ?? EMPTY_OBJECT,
      [assetData]
    );

    const isDeleted = Boolean(assetData?.deletedAt);

    const displayDate = isFolderAsset
      ? assetData?.insertedAt
      : getUploadDisplayDate(assetData);
    const formattedDisplayDate = displayDate
      ? TEXT.formattedDate(new Date(displayDate))
      : null;
    const folderItemCount =
      isFolderAsset && typeof assetData?.itemCount === 'number'
        ? assetData.itemCount
        : null;
    const folderItemCountLabel =
      typeof folderItemCount === 'number'
        ? TEXT.itemCountLabel({ count: folderItemCount })
        : null;
    const detailDateOrItemCount = isFolderAsset
      ? folderItemCountLabel
      : formattedDisplayDate;
    const durationInfo = useMemo(
      () => getAssetDurationInfo(assetData as QuickSearchAssetFragment),
      [assetData]
    );
    const videoDuration = durationInfo?.originalValue ?? 0;

    const baseMatches = useMemo(
      () => getValidAssetResultMatches(matches?.nodes ?? []),
      [matches?.nodes]
    );

    const hasMatches = baseMatches.length > 0;

    const visualMatch = useMemo(
      () =>
        baseMatches.find(
          (match) => match.__typename === 'VisualMatch'
        ) as VisualMatch,
      [baseMatches]
    );

    const posterFrameTime = useMemo(() => {
      const highestRelevancyClip = visualMatch?.clips?.find(
        (clip) => clip?.relevancyIndex === 0
      );

      return highestRelevancyClip
        ? convertMillisecondsToSeconds(highestRelevancyClip?.bestFrameTime ?? 0)
        : undefined;
    }, [visualMatch]);

    const fileType = getFileType(assetData as QuickSearchAssetFragment);

    const displayCategory = useMemo(
      () => getAssetDisplayCategory(fileType?.type),
      [fileType?.type]
    );

    const nameHighlight = useMemo(
      () =>
        (getFieldMatch<NameMatch>(baseMatches, 'NameMatch')?.terms as
          | string[]
          | undefined) ?? undefined,
      [baseMatches]
    );

    const breakpoint = useActiveWindowBreakpoint();
    const isMobile = breakpoint === 'base';

    const mediaHeight =
      isMobile || isCompact
        ? LIST_ITEM_MEDIA_HEIGHT_MOBILE
        : LIST_ITEM_MEDIA_HEIGHT;
    const mediaWidth =
      isMobile || isCompact
        ? LIST_ITEM_MEDIA_WIDTH_MOBILE
        : LIST_ITEM_MEDIA_WIDTH;
    const folderHeight =
      isMobile || isCompact ? FOLDER_HEIGHT_MOBILE : FOLDER_HEIGHT;
    const folderWidth =
      isMobile || isCompact ? FOLDER_WIDTH_MOBILE : FOLDER_WIDTH;

    const folderBackgroundColor = !isMobile
      ? theme.colors.background.primary
      : undefined;
    const folderWrapperStyle = useMemo(
      () =>
        ({
          '--folder-height': `${folderHeight}px`,
          '--folder-width': `${folderWidth}px`,
        }) as CSSProperties,
      [folderHeight, folderWidth]
    );

    const resultSource = getResultSourceFromSectionId(sectionId);

    useEffect(() => {
      if (isActive) {
        const resultType = isFolder ? 'Folder' : 'Asset';
        trackSearchResultsInspected?.({
          resultPosition: itemPosition,
          resultType,
          objectName: name,
          resultSource,
          matchType: getMatchTypeTrackingValues(matches?.nodes ?? []),
          tabSelected,
          webSource,
        });
      }
    }, [
      isActive,
      isFolder,
      itemPosition,
      matches?.nodes,
      name,
      resultSource,
      tabSelected,
      trackSearchResultsInspected,
      webSource,
    ]);

    const contextMenuContent = useMemo(
      () => (
        <AssetContextMenuContent
          assetId={assetId}
          itemPosition={itemPosition}
          sectionId={sectionId}
        />
      ),
      [assetId, itemPosition, sectionId]
    );
    const { contextMenu: builtInContextMenu, onContextMenu } =
      useListItemContextMenu({
        buttonAccessibilityLabel: TEXT.contextMenuLabel,
        content: contextMenuContent,
        enabled: shouldUseBuiltInContextMenu,
      });

    return (
      <ListItemCard
        contextMenu={builtInContextMenu}
        detailComponent={
          <ListItemDetailRow
            leftSegment={
              isRestrictedFolder ? (
                <div className={styles.restrictedFolderLabel}>
                  <Text color="tertiary" variant="caption">
                    {TEXT.restrictedFolder}
                  </Text>
                </div>
              ) : (
                <Text color="tertiary" variant="caption">
                  {fileType?.label}
                </Text>
              )
            }
            middleSegment={
              detailDateOrItemCount && (
                <Text color="tertiary" variant="caption">
                  {detailDateOrItemCount}
                </Text>
              )
            }
            rightSegment={
              <AssetPathname
                ancestorFolders={ancestorFolders as AncestorFolder[]}
                isDeleted={isDeleted}
                project={{ id: project?.id ?? '', name: project?.name ?? '' }}
              />
            }
          />
        }
        disableHoverStyles={disableHoverStyles}
        imageComponent={
          isFolder ? (
            <FolderMediaContainer
              height={mediaHeight}
              isCompact={isMobile || isCompact}
              width={mediaWidth}
            >
              <div className={styles.folderWrapper} style={folderWrapperStyle}>
                <FolderThumb
                  folderBackgroundColor={folderBackgroundColor}
                  folderId={assetId}
                  height={folderHeight}
                  variant="compact"
                  width={folderWidth}
                />
              </div>
              {isRestrictedFolder && (
                <div
                  aria-hidden="true"
                  className={styles.folderRestrictionBadge}
                >
                  <IconLock
                    color={theme.colors.components.badge.icon}
                    size={16}
                  />
                </div>
              )}
            </FolderMediaContainer>
          ) : (
            <ImageWrapper borderRadius={4} isCompact={isMobile || isCompact}>
              <MediaThumbnail
                assetTypename={assetData?.__typename ?? ''}
                backgroundColor="base"
                borderRadius={theme.radii[4]}
                contentBottom={
                  <MediaThumbnailContentBottom
                    contentBottom={
                      visualMatch ? (
                        <ClipMatchTimeline
                          clips={visualMatch.clips}
                          duration={videoDuration}
                        />
                      ) : undefined
                    }
                    contentTopLeft={
                      commentCount > 0 && (
                        <CommentCountLabel
                          aria-label={TEXT.commentCountLabel({
                            count: commentCount,
                          })}
                          count={commentCount}
                        />
                      )
                    }
                    contentTopRight={
                      durationInfo && (
                        <MediaThumbnailLabel>
                          {durationInfo.formattedValue}
                        </MediaThumbnailLabel>
                      )
                    }
                  />
                }
                contentTopRight={
                  isVersionStack ? (
                    <VersionNumberLabel
                      version={assetData?.versionNumber}
                      versionPrefixLabel={TEXT.versionPrefixLabel}
                    />
                  ) : undefined
                }
                displayCategory={displayCategory}
                height={mediaHeight}
                isImageFitPreferred={true}
                media={media}
                posterFrameTime={posterFrameTime}
                videoDuration={videoDuration}
                width={mediaWidth}
              />
            </ImageWrapper>
          )
        }
        isActive={isActive}
        isCompact={isCompact}
        matchesComponent={
          hasMatches && !isCompact ? (
            <FeaturedFieldMatches
              featuredMatchCount={featuredMatchCount}
              isActive={isActive}
              matches={baseMatches}
            />
          ) : null
        }
        onContextMenu={onContextMenu}
        paddingInlineEnd={paddingInlineEnd}
        titleComponent={
          name && (
            <HighlightText
              highlights={nameHighlight}
              text={name}
              truncate={true}
            />
          )
        }
      />
    );
  }
);

AssetListItem.displayName = 'AssetListItem';
